import { forwardRef, useState, useImperativeHandle, useEffect, useRef } from 'react';
import Checkboxes from '../Checkboxes';
import CustomSelect from '../CustomSelect';
import CustomDatePicker from '../CustomDatePicker';
import Input from '../Input';
import RadioButtons from '../RadioButtons';

// TODO: Read api.md

function getInitialValue(type: string, data: any) {
	switch (type) {
		case 'text':
		case 'number':
			return data.value;
		case 'radio':
			return data.value || null;
		case 'checkbox':
			return data.value || [];
		case 'select':
			return data.value;
		case 'date':
			return data.value ? new Date(data.value) : null;
	}
}

function DynamicFormElement(props: ComponentProps, ref: any) {
	const [value, _setValue] = useState<any>(getInitialValue(props.type, props));
	const inputRef = useRef<any>();

	function getValue() {
		switch (props.type) {
			case 'text':
			case 'number':
			case 'radio':
			case 'checkbox':
			case 'select':
			case 'date':
				return value;
			default:
				return null;
		}
	}

	function setValue(value: any) {
		switch (props.type) {
			case 'text':
			case 'number':
				_setValue(value);
				break;
			case 'radio':
				_setValue(value);
				inputRef.current.setValue(value);
				break;
			case 'checkbox':
				_setValue(value);
				inputRef.current.setValues(value);
				break;
			case 'select':
				_setValue(value);
				inputRef.current.setValue(value);
				break;
			case 'date':
				_setValue(value);
				inputRef.current.setValue(value);
				break;
			default:
				return null;
		}
	}

	function onChange(value: any, ...args: any) {
		switch (props.type) {
			case 'text':
			case 'number':
				const val = value.target.value;
				_setValue(val);
				props.onChange && props.onChange(val);
				break;
			case 'radio':
				_setValue(value);
				props.onChange && props.onChange(value);
				break;
			case 'checkbox':
				_setValue(args[1]);
				props.onChange && props.onChange(value, args[0], args[1]);
				break;
			case 'select':
				_setValue(value);
				props.onChange && props.onChange(value);
				break;
			case 'date':
				_setValue(value);
				props.onChange && props.onChange(value);
				break;
			default:
				return null;
		}
	}

	function onPropsChange(props: any) {
		_setValue((prevState: any) => getInitialValue(props.type, props));
	}

	useEffect(() => {
		onPropsChange(props);
	}, [props]);

	useImperativeHandle(
		ref,
		() => ({
			getValue,
			setValue,
		}),
		[value]
	);

	switch (props.type) {
		case 'text':
			return <Input {...props} value={value} type="text" ref={inputRef} onChange={onChange} />;
		case 'number':
			return <Input {...props} value={value} type="number" ref={inputRef} onChange={onChange} />;
		case 'radio':
			return <RadioButtons {...props} options={props.options} value={value} ref={inputRef} onChange={onChange} />;
		case 'checkbox':
			return (
				<Checkboxes
					{...props}
					options={props.options}
					selectedValues={value}
					ref={inputRef}
					onChange={onChange}
				/>
			);
		case 'select':
			return <CustomSelect {...props} options={props.options} value={value} ref={inputRef} onChange={onChange} menuPlacement="top" />;
		case 'date':
			return <CustomDatePicker {...props} value={value} ref={inputRef} onChange={onChange} />;
		default:
			return null;
	}
}

interface ComponentProps {
	type: 'text' | 'number' | 'radio' | 'checkbox' | 'select' | 'date';
	[key: string]: any;
}

export default forwardRef(DynamicFormElement);

type Option = {
	label: string;
	value: string;
};

type SelectOption = {
	label: string;
	value: string;
};
